import { useState, useEffect } from 'react';
import { ShieldCheck, Loader2, AlertCircle, CheckCircle2, ShieldOff } from 'lucide-react';
import { api } from '../../api/client';
import {
    signInWithGoogle,
    signInWithMicrosoft,
    type OAuthProvider,
} from '../../utils/oauthRecovery';
import {
    deriveKeyFromOAuthSub,
    encryptPrivateKey,
    generateSalt,
} from '../../utils/crypto';

interface Props {
    /** The raw (unencrypted) private key generated during password setup */
    privateKey: string;
    /** Called when the user successfully links a provider OR skips */
    onSuccess: () => void;
    /** Whether to show the 'Skip for now' button */
    allowSkip?: boolean;
}

interface OAuthConfig {
    google_client_id?: string;
    microsoft_client_id?: string;
    microsoft_tenant_id?: string;
}

export default function OAuthRecoveryStep({ privateKey, onSuccess, allowSkip = true }: Props) {
    const [config, setConfig] = useState<OAuthConfig | null>(null);
    const [isLoading, setIsLoading] = useState<OAuthProvider | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [linked, setLinked] = useState(false);

    useEffect(() => {
        api.getOAuthConfig()
            .then((cfg) => {
                setConfig(cfg);
                // If admin has not configured any provider, automatically skip this step
                if (!cfg.google_client_id && !cfg.microsoft_client_id) {
                    onSuccess();
                }
            })
            .catch(() => {
                // Network error — skip gracefully so signup isn't stuck
                setConfig({});
                onSuccess();
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleLink = async (provider: OAuthProvider) => {
        setError(null);
        setIsLoading(provider);
        try {
            // 1. Trigger OAuth popup
            let identity;
            if (provider === 'google') {
                identity = await signInWithGoogle(config!.google_client_id!);
            } else {
                identity = await signInWithMicrosoft(
                    config!.microsoft_client_id!,
                    config!.microsoft_tenant_id || 'common'
                );
            }

            // 2. Derive AES-GCM wrapping key from stable OAuth sub + fresh salt
            const saltPasskey = generateSalt();
            const wrappingKey = await deriveKeyFromOAuthSub(identity.sub, provider, saltPasskey);

            // 3. Encrypt the private key with the OAuth-derived key
            const encPasskeyBlob = await encryptPrivateKey(privateKey, wrappingKey);

            // 4. Upload to server — server verifies the id_token, stores the blob
            await api.savePasskeyBlob({
                enc_private_key_passkey: encPasskeyBlob,
                key_salt_passkey: saltPasskey,
                oauth_provider: provider,
                id_token: identity.id_token,
            });

            setLinked(true);
            setTimeout(() => onSuccess(), 1200);
        } catch (err: any) {
            setError(
                err?.response?.data?.error ||
                err?.message ||
                `Failed to link ${provider} account. Please try again.`
            );
        } finally {
            setIsLoading(null);
        }
    };

    // Still loading config
    if (!config) {
        return (
            <div style={{ textAlign: 'center', padding: 24 }}>
                <Loader2 size={24} className="spin" />
            </div>
        );
    }

    const hasGoogle = !!config.google_client_id;
    const hasMicrosoft = !!config.microsoft_client_id;

    // No providers — auto-skip already triggered in useEffect; render nothing
    if (!hasGoogle && !hasMicrosoft) return null;

    if (linked) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 12,
                padding: '24px 0',
                color: 'var(--color-success)',
            }}>
                <CheckCircle2 size={40} />
                <p style={{ fontWeight: 600, margin: 0 }}>Recovery account linked!</p>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Info banner */}
            <div className="auth-info">
                <ShieldCheck size={20} style={{ color: '#3b82f6', flexShrink: 0, marginTop: 1 }} />
                <div className="auth-info-content">
                    <span className="auth-info-title">Link a recovery account (recommended)</span>
                    <p className="auth-info-text">
                        If you forget your password, your linked Google or Microsoft account lets you
                        restore access to your encrypted messages — without involving your admin.
                        Your private key stays encrypted and is never shared with the provider.
                    </p>
                </div>
            </div>

            {error && (
                <div className="auth-error">
                    <AlertCircle size={16} />
                    <span>{error}</span>
                </div>
            )}

            {/* Provider buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {hasGoogle && (
                    <button
                        type="button"
                        className="auth-oauth-btn"
                        onClick={() => handleLink('google')}
                        disabled={!!isLoading}
                    >
                        {isLoading === 'google' ? (
                            <Loader2 size={18} className="spin" />
                        ) : (
                            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
                                <path fill="#4285F4" d="M43.6 20.3H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.1 8 2.9l5.7-5.7C34.5 6.5 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.5-.4-3.7z" />
                                <path fill="#34A853" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.8 1.1 8 2.9l5.7-5.7C34.5 7.2 29.6 5 24 5 16.3 5 9.7 9 6.3 14.7z" />
                                <path fill="#FBBC05" d="M24 43c5.4 0 10.2-1.9 13.8-5.1l-6.4-5.2C29.3 34.7 26.8 35.9 24 35.9c-5.2 0-9.5-3.3-11.1-8l-6.5 5C9.8 39.6 16.4 43 24 43z" />
                                <path fill="#EA4335" d="M43.6 20.3H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.7l6.4 5.2C40.5 36.4 44 30.6 44 24c0-1.3-.1-2.5-.4-3.7z" />
                            </svg>
                        )}
                        Continue with Google
                    </button>
                )}

                {hasMicrosoft && (
                    <button
                        type="button"
                        className="auth-oauth-btn"
                        onClick={() => handleLink('microsoft')}
                        disabled={!!isLoading}
                    >
                        {isLoading === 'microsoft' ? (
                            <Loader2 size={18} className="spin" />
                        ) : (
                            <svg width="18" height="18" viewBox="0 0 21 21" aria-hidden>
                                <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                                <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                                <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                                <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                            </svg>
                        )}
                        Continue with Microsoft
                    </button>
                )}
            </div>

            {/* Skip link — user can rely on admin escrow only */}
            {allowSkip && (
                <button
                    type="button"
                    className="auth-link"
                    onClick={onSuccess}
                    disabled={!!isLoading}
                    style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}
                >
                    <ShieldOff size={13} style={{ display: 'inline', marginRight: 4, verticalAlign: 'middle' }} />
                    Skip — I'll rely on admin recovery only
                </button>
            )}
        </div>
    );
}
